import type { PlaceKind } from '@bandido/shared/types';
import type { GeocodePrediction } from '@bandido/shared/types';

import {
  PlaceOption,
  PlaceRef,
  PlacesNoticeReason,
  placeOptionGroupLabelKey,
} from './places';
import {
  GeocodeNoticeReason,
  resolvedPointMatchesPrediction,
  shouldShowManualCoordsFields,
} from './places/geocode';
import { resolvePlacesPickerMode } from './placesPickerMode';

/**
 * El punto de un traslado se carga desde UN SOLO campo clasificado (#2227):
 * el asesor no elige antes "qué tipo de punto" es: escribe, y el desplegable
 * le ofrece juntos los lugares del maestro (aeropuertos, estaciones y
 * puertos, con su chip de tipo y su código) y las direcciones/hoteles del
 * resolver de Google. El TIPO deja de ser un paso previo y pasa a ser una
 * propiedad de lo que eligió.
 *
 * Acá vive la lógica pura de ese campo — qué control se dibuja, qué muestra,
 * qué opciones tiene y por qué no tiene ninguna. Es `.ts` a propósito: el
 * jest de este repo no resuelve `.tsx` (ver `jest.config.cjs` y el docstring
 * de `PlaceAutocomplete.tsx`), así que todo lo testeable tiene que poder
 * importarse sin arrastrar React.
 */

/**
 * Los tipos de lugar del MAESTRO que pueden ser punta de un traslado, igual
 * que antes de unificar el campo (`docs/RUTERO.md`): un traslado sale o
 * llega a un aeropuerto, una estación o un puerto. Una ciudad no es un punto
 * de recogida — aparece igual en la lista como CABECERA de sus aeropuertos
 * (`group: true`), que es lo que resuelve al asesor que no sabe el código.
 * Los hoteles y las direcciones no están en el maestro: esos vienen del
 * resolver, en el mismo desplegable.
 *
 * `terminal` va junto a `port` porque en el vocabulario del maestro es la
 * MISMA punta: son los muelles y las terminales de ferry (Battery Park Ferry
 * Port está clasificado `terminal`, no `port`). Las otras tres capas que
 * tocan este dato ya lo trataban así —`placeKindGroup` los manda al mismo
 * grupo, el rótulo dice "Puertos y terminales" y rutero mapea los dos a
 * `type: 'P'`—, y solo la consulta se había quedado sin pedirlo: el campo
 * prometía terminales que nunca podía traer.
 */
export const TRANSFER_POINT_PLACE_KINDS: PlaceKind[] = [
  'airport',
  'station',
  'port',
  'terminal',
];

/**
 * El tipo de punto del contrato de rutero: `A` aeropuerto, `H` alojamiento,
 * `S` estacion, `P` puerto.
 */
export type TransferPointType = 'A' | 'H' | 'S' | 'P';

/**
 * Que tipo DECLARA un punto — o ninguno. Sale del MODO en que se cargo, y de
 * nada mas: no es un dato guardado, no viaja en la URL y nadie lo elige.
 *
 * - **Por IATA es `A`, siempre.** El contrato lo marca obligatorio ahi ("un
 *   IATA es un aeropuerto: va con A") y el hub rechaza un IATA con cualquier
 *   otro tipo. Se deriva INCONDICIONALMENTE en vez de normalizar valores de a
 *   uno: mientras el tipo fuera un dato arrastrable —de la URL, del estado
 *   anterior— cualquier valor que la normalizacion no contemplara producia un
 *   payload que nuestro propio borde rechaza. Un `tipo=S` en un link viejo no
 *   describe un punto valido: describe uno roto.
 * - **Por COORDENADAS no se declara nada**, y eso es una respuesta, no un
 *   hueco. Las dos formas de que entre una coordenada dejan el tipo fuera del
 *   alcance de bandido: un lugar canonico (`plc_*`) viaja como
 *   `{kind: 'place'}` y lo clasifica el HUB con el `kind` del maestro, y una
 *   direccion resuelta no trae tipo porque el geocode le pide a Google
 *   unicamente `id` y `location` (decision del maestro, por costo y
 *   privacidad). Nadie miro que ES esa coordenada.
 *
 * El contrato dice textual que omitirlo "es lo que hay que hacer cuando no se
 * sabe si el lugar es un hotel, una estacion o un puerto: declarar un tipo
 * equivocado lo resuelve peor que no declarar ninguno" — medido contra
 * ineedtours, la misma coordenada sin tipo devuelve byte a byte lo mismo que
 * con el tipo correcto.
 */
export function transferPointType(
  mode: 'iata' | 'coords',
): TransferPointType | undefined {
  return mode === 'iata' ? 'A' : undefined;
}

/**
 * Qué control dibuja un punto. Tres estados EXCLUYENTES, en una sola
 * derivación (21.9): lo que el formulario valida (`isPointValid`) y lo que la
 * pantalla ofrece para escribir tienen que salir de la misma expresión, o
 * queda un punto que se exige y no se puede cargar.
 *
 * - `picker` — el campo unificado. Es el camino normal.
 * - `iata`   — el campo de texto de 3 letras. Solo cuando ya hay un código
 *              nativo cargado (mode-lock de `resolvePlacesPickerMode`: una
 *              selección hecha nunca se swapea por debajo) o con el setting
 *              de lugares apagado.
 * - `manual` — nombre/dirección/lat/lng a mano: fallback explícito del
 *              asesor, o setting apagado.
 *
 * Reusa las DOS reglas que ya existían (`resolvePlacesPickerMode` y
 * `shouldShowManualCoordsFields`) en vez de re-derivarlas: si un caso borde
 * de cualquiera de las dos se corrige, se corrige también acá.
 */
export type TransferPointControl = 'picker' | 'iata' | 'manual';

export function transferPointControl({
  mode,
  placesEnabled,
  hasIata,
  hasPlace,
  useManualFields,
}: {
  mode: 'iata' | 'coords';
  placesEnabled: boolean;
  hasIata: boolean;
  hasPlace: boolean;
  useManualFields: boolean;
}): TransferPointControl {
  if (mode === 'iata') {
    return resolvePlacesPickerMode({
      settingEnabled: placesEnabled,
      hasNativeSelection: hasIata,
      hasPlaceSelection: hasPlace,
    })
      ? 'picker'
      : 'iata';
  }
  return shouldShowManualCoordsFields({ useManualFields, placesEnabled }) ? 'manual' : 'picker';
}

/**
 * ¿Se dibuja el selector de "ubicar por"? SOLO con el setting de lugares
 * apagado (#2227).
 *
 * Con el setting prendido no hay ningún paso previo: el tipo del punto sale
 * de lo que el asesor elige en el campo unificado. Con el setting apagado no
 * existe ese campo —no hay maestro ni resolver que consultar— y las dos
 * formas de carga a mano son incompatibles entre sí (un código IATA o un
 * nombre con coordenadas), así que ahí el tipo SÍ hay que declararlo: sin el
 * selector, el origen solo podría cargarse por código y el destino solo por
 * coordenadas, que es capacidad que hoy existe y no se puede perder por una
 * caída del setting.
 */
export function shouldShowPointModeToggle({
  placesEnabled,
}: {
  placesEnabled: boolean;
}): boolean {
  return !placesEnabled;
}

/** Lo que el campo unificado muestra en reposo: un lugar del maestro o una dirección resuelta. */
export type TransferPointValue =
  | { source: 'place'; place: PlaceRef }
  | { source: 'address'; label: string };

/**
 * El valor a mostrar, derivado del punto del formulario. UNA derivación para
 * los dos orígenes posibles: el `plc_*` elegido manda, y si no hay, la
 * dirección resuelta se muestra por su dirección (o su nombre, cuando el
 * punto viene de un link viejo que solo trae nombre).
 */
export function transferPointValue({
  placePoint,
  address,
  name,
}: {
  placePoint: PlaceRef | null;
  address: string;
  name: string;
}): TransferPointValue | null {
  if (placePoint) return { source: 'place', place: placePoint };
  const label = address.trim() || name.trim();
  return label ? { source: 'address', label } : null;
}

/** Una opción del desplegable: viene del maestro de lugares o del resolver de direcciones. */
export interface TransferPointPlaceOption {
  source: 'place';
  /** Id ÚNICO en la lista mezclada — prefijado por fuente, no el id crudo. */
  id: string;
  place: PlaceOption;
}

export interface TransferPointAddressOption {
  source: 'address';
  id: string;
  description: string;
  /**
   * El id opaco de Google, para el paso 2 (`resolve`). Vacío SOLO en la
   * opción sintética que representa el valor ya elegido (que nunca está en
   * la lista y por lo tanto nunca se puede volver a elegir).
   */
  placeId: string;
}

export type TransferPointOption = TransferPointPlaceOption | TransferPointAddressOption;

const PLACE_OPTION_ID_PREFIX = 'plc:';
const ADDRESS_OPTION_ID_PREFIX = 'addr:';

/**
 * Las dos fuentes en una sola lista: primero los lugares del maestro (ya
 * ordenados por familia visual por `sortPlaceOptionsForDisplay`), después las
 * direcciones. MUI agrupa por posición, así que el orden de esta lista ES el
 * orden de las secciones.
 *
 * Los ids se prefijan por fuente: las dos fuentes son catálogos distintos
 * (un `plc_*` y un id opaco de Google) y la lista mezclada necesita un id
 * único, que además es lo que `isOptionEqualToValue` compara.
 */
export function buildTransferPointOptions({
  places,
  predictions,
}: {
  places: PlaceOption[];
  predictions: GeocodePrediction[];
}): TransferPointOption[] {
  return [
    ...places.map<TransferPointOption>((place) => ({
      source: 'place',
      id: `${PLACE_OPTION_ID_PREFIX}${place.id}`,
      place,
    })),
    ...predictions.map<TransferPointOption>((prediction) => ({
      source: 'address',
      id: `${ADDRESS_OPTION_ID_PREFIX}${prediction.placeId}`,
      description: prediction.description,
      placeId: prediction.placeId,
    })),
  ];
}

/**
 * La opción que representa al valor ya elegido. No sale de la lista: MUI
 * necesita un objeto con el shape de las opciones para dibujar el valor
 * controlado, y un punto elegido tiene que verse aunque la búsqueda que lo
 * encontró ya no esté (restaurar un link, invertir origen y destino).
 */
export function transferPointSelectedOption(
  value: TransferPointValue | null,
): TransferPointOption | null {
  if (!value) return null;
  if (value.source === 'place') {
    const { place } = value;
    return {
      source: 'place',
      id: `${PLACE_OPTION_ID_PREFIX}${place.id}`,
      place: {
        id: place.id,
        kind: place.kind,
        name: place.name,
        ...(place.code ? { code: place.code } : {}),
      },
    };
  }
  return {
    source: 'address',
    id: `${ADDRESS_OPTION_ID_PREFIX}selected`,
    description: value.label,
    placeId: '',
  };
}

/** La etiqueta de una opción — la misma que el input muestra en reposo. */
export function transferPointOptionLabel(option: TransferPointOption): string {
  return option.source === 'place' ? option.place.name : option.description;
}

/**
 * El encabezado de sección. Los lugares reusan las familias del maestro
 * (`Aeropuertos`, `Estaciones`, …) y las direcciones tienen la suya: son
 * familias VISUALES, no vocabulario de proveedor, y nada de esto viaja al
 * backend.
 */
export function transferPointOptionGroupLabelKey(option: TransferPointOption): string {
  return option.source === 'place'
    ? placeOptionGroupLabelKey(option.place)
    : 'travelServices.places.groups.addresses';
}

/**
 * ¿Los DOS puntos están listos para buscar?
 *
 * "Válido" no alcanza: mientras una dirección elegida se resuelve, el punto
 * que sigue en el formulario es el ANTERIOR (el nuevo no existe hasta que el
 * proveedor devuelve sus coordenadas). Sin este gate, el asesor elige una
 * dirección y aprieta Buscar en el mismo segundo, y la búsqueda sale con el
 * punto viejo — el que él acaba de reemplazar. El punto anterior se conserva
 * para mostrarlo y para volver atrás si el resolve falla, nunca para mandarlo
 * como si fuera la selección nueva (21.23).
 *
 * Es la MISMA condición que bloquea invertir origen y destino: una sola
 * derivación de "hay un punto resolviéndose", no dos que puedan desalinearse.
 *
 * Ojo con lo que NO bloquea: un resolve FALLADO no deja nada resolviéndose,
 * así que se puede buscar de nuevo — y con razón, porque el campo volvió a
 * mostrar el punto anterior. Lo que se busca es exactamente lo que el asesor
 * está viendo.
 */
export function transferPointsReady({
  originValid,
  destinationValid,
  anyPointResolving,
}: {
  originValid: boolean;
  destinationValid: boolean;
  anyPointResolving: boolean;
}): boolean {
  return originValid && destinationValid && !anyPointResolving;
}

// ── Intento de geocode: sesión de tipeo y resolución ─────────────────────
// El resolver de direcciones tiene DOS piezas de estado que no son del
// autocomplete —la sesión de Google y el resultado del `resolve`— y las dos
// tienen que morir cuando el asesor cierra una sesión de tipeo o elige otra
// cosa. Viven acá, puras y testeables, porque el jest de este repo no resuelve
// `.tsx` a propósito: el componente solo las cablea.

/**
 * Qué sesión de Google corresponde AHORA. La regla es "un UUID por sesión de
 * TIPEO" (`docs/PLACES.md` §9; sin eso Google factura cada pulsación por
 * separado), y "sesión de tipeo" no se redefine acá: es el `editing` de
 * `autocompleteFocus.ts`, la misma definición que usan todos los pickers del
 * buscador.
 *
 * Elegir una opción CIERRA la sesión de tipeo sin devolver el foco
 * (invariante 4 de `autocompleteFocus.ts`), así que lo que se teclee después
 * empieza una sesión nueva —y por lo tanto pide un token nuevo— sin pasar por
 * un blur. Reusar el token entre esas dos búsquedas las factura como una sola
 * y contradice la semántica del proveedor.
 */
export function nextGeocodeSessionToken({
  editing,
  current,
  createToken,
}: {
  editing: boolean;
  current: string | null;
  createToken: () => string;
}): string | null {
  if (!editing) return null;
  return current ?? createToken();
}

/**
 * El intento de resolver una dirección elegida. Tres estados EXCLUYENTES, no
 * dos booleanos sueltos: `resolving` bloquea el campo y el swap, y `failed`
 * es un aviso PERSISTENTE que además ofrece la carga manual — dejarlo colgado
 * después de que el asesor eligió otra cosa muestra un punto válido como
 * fallado.
 */
export type TransferPointResolveState =
  | { status: 'idle' }
  /**
   * `resetKey` es la generación de puntos con la que ARRANCÓ este intento
   * (ver el prop del mismo nombre en `TransferPointAutocomplete`). Viaja en
   * el estado para que la respuesta pueda compararla con la generación
   * vigente: así, que el intento esté vencido no depende de que el campo ya
   * se haya re-renderizado. "Limpiar" es un evento discreto y React flushea
   * su render antes de cualquier callback de red, pero apoyar la corrección
   * en ese orden es apoyarla en una carrera.
   */
  | { status: 'resolving'; placeId: string; resetKey: number }
  | { status: 'failed' };

export const TRANSFER_POINT_RESOLVE_IDLE: TransferPointResolveState = { status: 'idle' };

export type TransferPointResolveEvent =
  /** El asesor eligió una dirección: arranca el paso 2. */
  | { type: 'resolveStarted'; placeId: string; resetKey: number }
  /** Se comiteó una selección (un lugar del maestro, o la X que vacía el campo). */
  | { type: 'selectionCommitted' }
  /**
   * El punto se reemplazó DESDE AFUERA del campo — hoy, "Limpiar" la
   * búsqueda. El formulario descarta el punto pero el campo sigue montado
   * con su resolve en vuelo, así que sin esto la respuesta que llega después
   * repuebla un origen o un destino que el asesor borró a propósito.
   */
  | { type: 'pointReplacedExternally' };

/**
 * El estado que deja cada transición que el asesor provoca. No recibe el
 * estado anterior porque ninguna de las tres depende de él: arrancar un
 * resolve, comitear una selección o que le reemplacen el punto son absolutas.
 * Lo que sí depende del estado —si una respuesta que llega tarde vale o no—
 * vive en `transferPointResolveSettled`.
 */
export function transferPointResolveStateAfter(
  event: TransferPointResolveEvent,
): TransferPointResolveState {
  // Sin `default` a propósito: un evento nuevo tiene que romper la compilación.
  switch (event.type) {
  case 'resolveStarted':
    return { status: 'resolving', placeId: event.placeId, resetKey: event.resetKey };
  case 'selectionCommitted':
    // Elegir un lugar del maestro (o vaciar el campo con la X) cierra el
    // intento anterior: el punto que el asesor tiene ahora es válido, y
    // seguir mostrando "no se pudo resolver esa dirección" lo desmiente.
    return TRANSFER_POINT_RESOLVE_IDLE;
  case 'pointReplacedExternally':
    // Mismo destino que `selectionCommitted` pero por otra causa, y por eso
    // es un evento aparte: acá el campo ni siquiera participó. Deja el
    // intento cerrado, así que cualquier respuesta posterior es tardía.
    return TRANSFER_POINT_RESOLVE_IDLE;
  }
}

/**
 * Qué hacer cuando el paso 2 TERMINA — con respuesta o con error. Devuelve el
 * estado nuevo Y si el punto se comitea, en UNA sola decisión: si el "se
 * comitea" se dedujera aparte (por ejemplo de que el estado quedó `idle`),
 * una respuesta vencida —que también deja `idle`— repoblaría el punto que el
 * asesor acaba de descartar.
 *
 * `currentResetKey` es la generación de puntos VIGENTE. Compararla con la que
 * guardó el intento es lo que hace que el descarte no dependa de un orden:
 * "Limpiar" o invertir origen y destino vencen el intento en el instante en
 * que ocurren, aunque la respuesta llegue antes de que el campo se entere por
 * un re-render.
 *
 * Cuatro desenlaces:
 * - el intento ya no está en curso, o arrancó en otra generación → vencido:
 *   ni comitea ni falla, y el estado vuelve a `idle` para que el campo no
 *   quede bloqueado esperando una respuesta que ya no importa;
 * - error del proveedor → falla;
 * - resolvió OTRO lugar → falla, aunque HTTP diga 200: el punto mezclaría la
 *   dirección de una identidad con las coordenadas de otra, y el traslado se
 *   reserva contra las coordenadas;
 * - resolvió el pedido → se comitea.
 */
export function transferPointResolveSettled(
  state: TransferPointResolveState,
  outcome: { type: 'succeeded'; placeId: string | undefined } | { type: 'failed' },
  currentResetKey: number,
): { state: TransferPointResolveState; commit: boolean } {
  if (state.status !== 'resolving') return { state, commit: false };
  if (state.resetKey !== currentResetKey) {
    return { state: TRANSFER_POINT_RESOLVE_IDLE, commit: false };
  }
  if (outcome.type === 'failed') return { state: { status: 'failed' }, commit: false };
  return resolvedPointMatchesPrediction(state.placeId, outcome.placeId)
    ? { state: TRANSFER_POINT_RESOLVE_IDLE, commit: true }
    : { state: { status: 'failed' }, commit: false };
}

/**
 * En qué estado está la autoridad de conectores para el maestro de lugares.
 * Cuatro estados EXCLUYENTES, porque `connectorCapabilitiesAreActionable`
 * (`isSuccess && !isFetching`) los colapsa en un booleano y `placesNoticeReason`
 * los recibe ya colapsados como `disabled`:
 *
 * - `pending`     — la query está en vuelo, o todavía no resolvió por primera
 *                   vez. El maestro no está caído: todavía no contestó, y
 *                   anunciarlo sería una alarma falsa en cada carga de la
 *                   pantalla y en cada refetch al volver a la pestaña (ese
 *                   query usa `refetchOnWindowFocus: 'always'`).
 * - `failed`      — la query TERMINÓ con error (`retry: false`). El maestro
 *                   queda inconsultable, y eso hay que decirlo: si no, el
 *                   campo muestra solo direcciones, o "sin resultados", como
 *                   si esa fuente estuviera sana.
 * - `noConnector` — respondió y la agencia no tiene ningún conector de
 *                   traslados: el maestro no se consulta (fail-closed).
 * - `ready`       — respondió y hay con qué filtrar.
 *
 * `pending` gana sobre `failed` mientras hay un reintento en vuelo: ahí el
 * estado todavía se está resolviendo.
 */
export type TransferPointPlacesAuthority = 'pending' | 'failed' | 'noConnector' | 'ready';

export function transferPointPlacesAuthority({
  actionable,
  isFetching,
  isError,
  servedByCount,
}: {
  /**
   * `connectorCapabilitiesAreActionable` — la regla COMPARTIDA de cuándo la
   * autoridad se puede usar. Se recibe ya evaluada en vez de re-derivarla
   * acá: `ready` tiene que significar exactamente lo mismo que en el resto
   * del epic, y esta función solo parte en dos el caso en que NO es usable.
   */
  actionable: boolean;
  isFetching: boolean;
  isError: boolean;
  servedByCount: number;
}): TransferPointPlacesAuthority {
  if (!actionable) return isError && !isFetching ? 'failed' : 'pending';
  return servedByCount > 0 ? 'ready' : 'noConnector';
}

/**
 * Por qué el campo no tiene (o no puede tener) lo que el asesor busca.
 * Estados EXCLUYENTES, nunca un booleano: el campo tiene DOS fuentes que
 * fallan por separado, y callarse la que se cayó es exactamente el fallo
 * silencioso que deja al asesor buscando un hotel en una lista que solo
 * puede traer aeropuertos.
 *
 * - `resolving`            — resolviendo la dirección elegida.
 * - `resolveError`         — el resolve falló (o devolvió otro lugar).
 * - `noSources`            — ninguna de las dos fuentes puede contestar.
 * - `placesDisabled`       — la autoridad respondió y no hay conector de
 *                            traslados: el maestro no se consulta
 *                            (fail-closed), solo hay direcciones.
 * - `placesError`          — el maestro no se puede consultar: falló su
 *                            búsqueda, o falló la autoridad de conectores
 *                            que la habilita. Solo hay direcciones.
 * - `addressesNotEnabled` / `addressesBudgetExceeded` / `addressesError` —
 *                            solo hay lugares, y por qué: el resolver está
 *                            apagado, sin cupo por hoy, o falló. Son tres
 *                            estados distintos porque para el asesor lo son:
 *                            el cupo vuelve mañana, el error se reintenta y
 *                            lo apagado no se arregla escribiendo.
 * - `tooShort`             — falta escribir para poder buscar.
 * - `empty`                — se buscó en las dos y no hay nada.
 * - `null`                 — hay resultados y las dos fuentes están sanas.
 */
export type TransferPointNotice =
  | 'resolving'
  | 'resolveError'
  | 'noSources'
  | 'placesDisabled'
  | 'placesError'
  | 'addressesNotEnabled'
  | 'addressesBudgetExceeded'
  | 'addressesError'
  | 'tooShort'
  | 'empty'
  | null;

export function transferPointNotice({
  resolving,
  resolveFailed,
  placesAuthority,
  placesReason,
  addressesReason,
  hasOptions,
}: {
  resolving: boolean;
  resolveFailed: boolean;
  /**
   * Estado de la autoridad de conectores, que es lo que habilita consultar el
   * maestro. Va aparte de `placesReason` porque `placesNoticeReason` recibe
   * un solo booleano (`ready`) y no puede distinguir "cargando" de "falló" ni
   * de "no hay conector" — tres cosas con consecuencias distintas para el
   * asesor.
   */
  placesAuthority: TransferPointPlacesAuthority;
  placesReason: PlacesNoticeReason;
  addressesReason: GeocodeNoticeReason;
  hasOptions: boolean;
}): TransferPointNotice {
  if (resolving) return 'resolving';
  if (resolveFailed) return 'resolveError';
  // Una fuente caída se avisa AUNQUE haya opciones de la otra: la lista
  // parcial se ve igual de sana que la completa, y el asesor no tiene cómo
  // saber que lo que busca no puede aparecer.
  const placesDown =
    placesAuthority === 'failed' ||
    placesAuthority === 'noConnector' ||
    (placesAuthority === 'ready' && placesReason === 'error');
  const addressesDown =
    addressesReason === 'error' ||
    addressesReason === 'notEnabled' ||
    addressesReason === 'budgetExceeded';
  if (placesDown && addressesDown) return 'noSources';
  if (placesDown) {
    return placesAuthority === 'noConnector' ? 'placesDisabled' : 'placesError';
  }
  if (addressesReason === 'notEnabled') return 'addressesNotEnabled';
  if (addressesReason === 'budgetExceeded') return 'addressesBudgetExceeded';
  if (addressesReason === 'error') return 'addressesError';
  if (hasOptions) return null;
  // Fuera de `ready`, `placesReason` es `disabled` y no dice nada de la
  // longitud de lo tipeado: la única señal que queda es la del resolver.
  if (placesAuthority !== 'ready') {
    return addressesReason === 'tooShort' ? 'tooShort' : 'empty';
  }
  // El mínimo del maestro (2) es más corto que el del resolver (3): mientras
  // el maestro todavía no puede buscar, no se buscó en ninguna de las dos.
  if (placesReason === 'tooShort') return 'tooShort';
  return 'empty';
}

/**
 * ¿El aviso se muestra SIEMPRE (debajo del campo) o solo con el desplegable
 * abierto? Lo que describe el estado del servicio se muestra siempre: el
 * asesor tiene que enterarse de que una fuente se cayó sin tener que abrir la
 * lista para descubrirlo.
 */
export function transferPointNoticeIsPersistent(notice: TransferPointNotice): boolean {
  return (
    notice === 'resolving' ||
    notice === 'resolveError' ||
    notice === 'noSources' ||
    notice === 'placesDisabled' ||
    notice === 'placesError' ||
    notice === 'addressesNotEnabled' ||
    notice === 'addressesBudgetExceeded' ||
    notice === 'addressesError'
  );
}
